import { memo } from 'react';
import { Handle, Node, NodeProps, Position, useConnection } from '@xyflow/react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper'
import { NotStarted, StopCircle, AccountTree, PlayArrow, Settings } from '@mui/icons-material';

export type EditableNodeType = Node<{
    internalId: string,
    name: string,
    labelKey: string,
    initial: boolean,
    final: boolean,
    color: string,
    transitionEditMode: boolean,
    extraAttributes: any,
    editScopes: string,
    viewScopes: string,
    deleteScopes: string,
    automaticAction?: {
        stateType: string,
        config: any
    }
}, 'editableNode'>;

const EditableNode = memo(({ data }: NodeProps<EditableNodeType>) => {
    const connection = useConnection();
    const isTarget = connection.inProgress && connection.fromNode.id !== data.internalId;
    const isConnecting = !isTarget && connection.toNode?.id === data.internalId;

    const theme = useTheme();
    
    // Function to determine if this is an automatic action node
    const isAutomaticAction = () => {
        // Check if node has automaticAction configuration
        if (data.automaticAction?.stateType) {
            return true;
        }
        
        // Check extraAttributes for automatic flag
        if (data.extraAttributes?.automatic === true) {
            return true;
        }
        
        // Check for naming patterns that suggest automatic actions
        const name = (data.name || '').toLowerCase();
        const labelKey = (data.labelKey || '').toLowerCase();
        
        const automaticKeywords = [
            'automatic', 'automatico', 'auto', 'system', 'automated',
            'trigger', 'webhook', 'api', 'batch', 'scheduled', 'cron',
            'email', 'notification', 'pdf', 'lambda', 'sqs', 's3'
        ];
        
        return automaticKeywords.some(keyword => 
            name.includes(keyword) || labelKey.includes(keyword)
        );
    };
    
    return (
        <div className="custonNode">
            {data?.extraAttributes?.virtualInitialNode || data?.extraAttributes?.virtualFinalNode ? (
                <>
                    {data?.extraAttributes?.virtualInitialNode && <NotStarted />}
                    {data?.extraAttributes?.virtualFinalNode && <StopCircle />}
                </>
            ) : 
            <Box className="paperContainer">
                <Paper
                    variant='outlined'
                    className={`d-flex ${isTarget?'target':'targeting'} ${isConnecting?'connecting':'no-connecting'} ${connection.inProgress?'connectionInProgess':''}`}
                    sx={{
                        backgroundColor: '#fff',
                        color: '#333',
                        borderColor: '#e0e0e0',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        minWidth: '120px',
                        minHeight: '64px', // Double the height (from ~32px to 64px)
                        display: 'flex',
                        alignItems: 'flex-start', // Align to top instead of center
                        gap: '8px'
                    }}
                >
                    {/* Icon section */}
                    <Box 
                        sx={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '4px',
                            backgroundColor: data.color || '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}
                    >
                        {data.initial ? (
                            <PlayArrow sx={{ fontSize: '14px', color: '#fff' }} />
                        ) : data.final ? (
                            <StopCircle sx={{ fontSize: '14px', color: '#fff' }} />
                        ) : isAutomaticAction() ? (
                            <Settings sx={{ fontSize: '14px', color: '#fff' }} />
                        ) : (
                            <AccountTree sx={{ fontSize: '14px', color: '#fff' }} />
                        )}
                    </Box>
                    
                    {/* Text content */}
                    <Box sx={{ 
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center', // Vertically center the text with the icon
                        height: '24px' // Match the icon height for perfect alignment
                    }}>
                        <Typography 
                            sx={{ 
                                fontSize: '13px',
                                fontWeight: 500,
                                lineHeight: 1.2,
                                color: '#333'
                            }}
                        >
                            {(data?.labelKey && intl.get(data?.labelKey)) || data?.name}
                        </Typography>
                    </Box>
                </Paper>
            </Box>
            }
                        
            {/* Source handles - two for automatic actions (success/error), one for regular nodes */}
            {isAutomaticAction() ? (
                // Automatic actions always show both handles (success and error)
                <>
                    {/* Success handle (green) - positioned at top right */}
                    <Handle 
                        id="success"
                        className="customHandle" 
                        position={Position.Right} 
                        type="source"
                        isConnectable={true}
                        style={{
                            top: '30%',
                            background: '#4caf50',
                            border: '2px solid #2e7d32',
                            width: '12px',
                            height: '12px',
                            cursor: 'crosshair'
                        }}
                    />
                    {/* Error handle (red) - positioned at bottom right */}
                    <Handle 
                        id="error"
                        className="customHandle" 
                        position={Position.Right} 
                        type="source"
                        isConnectable={true}
                        style={{
                            top: '70%',
                            background: '#f44336',
                            border: '2px solid #c62828',
                            width: '12px',
                            height: '12px',
                            cursor: 'crosshair'
                        }}
                    />
                </>
            ) : (!connection.inProgress) && data.transitionEditMode ? (
                <Handle className="customHandle" position={Position.Right} type="source"/>
            ) : (
                <Handle className="d-none" position={Position.Right} type="source"/>
            )}
            
            {/* We want to disable the target handle, if the connection was started from this node */}
            {(!connection.inProgress || isTarget) && (
                <Handle className="customHandle" position={Position.Left} type="target" isConnectableStart={false} />
            )}
        </div>
    );
});
EditableNode.displayName = 'EditableNode';
export default EditableNode;